import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'next-i18next';
import clsx from 'clsx';
import { AlertOrderType } from '@interfaces/models/alert';
import Button from '@components/atoms/button/button';
import usePageType from '@hooks/use-page-type';
import useOnClickOutside from '@hooks/browser-events/use-on-click-outside';
import useOnKeyPress from '@hooks/browser-events/use-on-key-press';
import { ElasticSearch } from '@interfaces/models/elasticSearch';
import useWindowSize from '@hooks/use-window-size';
import { useFocusRestore } from '@hooks/a11y/use-focus-restore';
import SortOptionsList from '@components/catalog/sort/sort-options-list';
import Icon from '@components/atoms/icon/icon';
import styles from './sort.module.scss';

type SortOptionValue = ElasticSearch['sortBy'] | AlertOrderType;

export type SortOptions = {
  text: string;
  value: SortOptionValue;
};

type SortProps = {
  defaultValue?: SortOptionValue;
  onChange?: (type: SortOptionValue) => void;
  open: boolean;
  openDialog: (isOpen: boolean) => void;
  options: SortOptions[];
  fullScreenOnMobile?: boolean;
  handleClickOnButton?: (open: boolean) => void;
} & React.HTMLProps<HTMLDivElement>;

const Sort: React.FC<SortProps> = (props) => {
  const { defaultValue, onChange, open, openDialog, options, fullScreenOnMobile = true, handleClickOnButton } = props;

  const { t } = useTranslation(['common', 'a11y']);
  const { isWiderThanMd } = useWindowSize();
  const { pageType } = usePageType();
  const buttonRef = useRef<HTMLButtonElement>(null);

  const selectRef = useRef<HTMLDivElement>(null);

  const escPress = useOnKeyPress('Escape', selectRef);
  useOnClickOutside(selectRef, () => {
    openDialog(false);
  });

  // Restore focus to sort button when dropdown closes
  useFocusRestore(open, { onAfterRestore: () => buttonRef.current?.focus() });

  const [selectedValue, setSelectedValue] = useState(defaultValue);

  // Sync selectedValue when defaultValue changes (e.g., async updates on alerts page)
  useEffect(() => {
    setSelectedValue(defaultValue);
  }, [defaultValue]);

  const toggleDialog = () => {
    openDialog(!open);
    handleClickOnButton?.(!open);
  };

  const handleChange = (value: ElasticSearch['sortBy']) => {
    setSelectedValue(value);
    // We update on change and close the dropdown
    // only on large devices or small device when the dropdown is not full screen,
    // on others, user must manually apply by clicking the CTA for change and close
    if (isWiderThanMd || !fullScreenOnMobile) {
      onChange(value);
      openDialog(false);
    }
  };

  const applyChange = () => {
    onChange(selectedValue);
    openDialog(false);
  };

  useEffect(() => {
    if (escPress) {
      openDialog(false);
    }
  }, [escPress]);

  const sortLabel = t('CATALOG.FILTERS.SORT.SORT_BY');

  return (
    <div
      className={styles.catalogSort}
      ref={selectRef}
    >
      <span className="vc-d-none vc-d-md-inline-flex">{t('CATALOG.FILTERS.SORT.SORT_BY')}</span>
      <Button
        data-cy="catalog-sort-popup-toggle-button"
        disableDefaultStyling
        className={clsx(styles.catalogSort__button, {
          ['vc-d-none vc-d-md-inline-flex']: fullScreenOnMobile,
          [styles.open]: open,
        })}
        onClick={() => toggleDialog()}
        aria-expanded={open}
        aria-controls={`${pageType}-sort-options`}
        ref={buttonRef}
      >
        <span className={clsx(styles.catalogSort__button__textLabel, { 'vc-d-md-none': !fullScreenOnMobile })}>
          {sortLabel}
        </span>

        <span
          className={!fullScreenOnMobile && 'vc-d-none vc-d-md-inline-block'}
          data-cy="catalog-sort-popup-toggle-button-text"
        >
          {options.find((i) => i.value === selectedValue)?.text}
        </span>
        <Icon
          name="small-dropdown-down-default"
          className={styles.catalogSort__button__icon}
          ariaLabel={open ? t('A11Y.ICON.DROPDOWN_UP', { ns: 'a11y' }) : t('A11Y.ICON.DROPDOWN_DOWN', { ns: 'a11y' })}
        />
      </Button>
      <div
        className={clsx(
          styles.catalogSort__list,
          { [styles.open]: open },
          { [styles['catalogSort__list--full']]: fullScreenOnMobile },
        )}
        id={`${pageType}-sort-options`}
      >
        <div className={clsx(styles.catalogSort__listHeader, { 'vc-d-none': !fullScreenOnMobile })}>
          <span>{t('CATALOG.FILTERS.SORT.TITLE')}</span>
          <Button
            data-cy="catalog-sort-popup-close-button"
            disableDefaultStyling
            aria-label="close modal"
            className="vc-modal__closeBtn"
            onClick={() => toggleDialog()}
          >
            <span className="vc-modal__closeBtn__crossLeft" />
            <span className="vc-modal__closeBtn__crossRight" />
          </Button>
        </div>
        <div className={styles.catalogSort__listContent}>
          <SortOptionsList<SortOptionValue>
            selectedValue={selectedValue}
            handleChange={handleChange}
            options={options}
          />
        </div>
        {fullScreenOnMobile && (
          <div className={styles.catalogSort__listAction}>
            <Button
              variant="primary"
              size="large"
              fullWidth
              onClick={applyChange}
              data-cy="catalog-sort-popup-confirm-button"
            >
              {t('CATALOG.FILTERS.SORT.VALIDATE_BUTTON')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sort;
